/**
 * Furniture alike, as the app tells it when it replaces a piece (`ReplaceSimilarFurniture`, `ReplaceAllSameTypeFurniture`):
 *
 * - the catalog list a piece's similar models are in — the one its first two tags that are not options lead to
 *   (`FurnitureInfoDatasource.GetSimilarItemsViewPath`), the first tag's list before the second's, the narrower list
 *   before the wider —, which «Заменить» opens on;
 * - whether another piece is of the same kind: the first piece's list takes it, or the two share a tag that is not an
 *   option (`MatchesOriginal`), which «Для всех (N)» counts in the piece's room.
 *
 * Option tags are the ones the catalog's chips narrow a list by — colours, styles, single or double — and that define
 * no list of their own.
 */
import type { Item, Level } from '../core/hp/types';
import { roomHolds } from '../core/geometry/edit';
import { catalogTree, type CatalogNode, type TagQuery } from './catalog';

let cache: { lists: CatalogNode[]; options: Set<string> } | undefined;
function catalogIndex() {
  if (cache) return cache;
  const lists: CatalogNode[] = [];
  const walk = (nodes: readonly CatalogNode[]) => { for (const node of nodes) node.children ? walk(node.children) : lists.push(node); };
  walk(catalogTree());
  const defining = new Set(lists.flatMap(list => [...list.anyTags ?? [], ...list.allTags ?? []]));
  const options = new Set(lists.flatMap(list => (list.filters ?? []).flatMap(filter => [...filter.anyTags, ...filter.allTags])).filter(tag => !defining.has(tag)));
  return cache = { lists, options };
}

const takes = (query: Partial<TagQuery>, tags: readonly string[]) =>
  (query.allTags ?? []).every(tag => tags.includes(tag)) && (!query.anyTags?.length || query.anyTags.some(tag => tags.includes(tag)));

/** The tags of a piece that say what it is, in its own order. */
export const kindTags = (tags: readonly string[]): string[] => tags.filter(tag => !catalogIndex().options.has(tag));

/** The catalog list of a piece's similar models, by the tree's id; none when its tags lead nowhere. */
export function similarList(tags: readonly string[]): CatalogNode | undefined {
  const lead = kindTags(tags).slice(0, 2);
  if (!lead.length) return undefined;
  const own = (list: CatalogNode) => [...list.anyTags ?? [], ...list.allTags ?? []];
  return catalogIndex().lists
    .map((list, order) => ({ list, order, rank: lead.findIndex(tag => own(list).includes(tag)) }))
    .filter(({ list, rank }) => rank >= 0 && takes(list, tags))
    .sort((a, b) => a.rank - b.rank || (b.list.allTags?.length ?? 0) - (a.list.allTags?.length ?? 0) || (a.list.anyTags?.length ?? 0) - (b.list.anyTags?.length ?? 0) || a.order - b.order)[0]?.list;
}

/** Whether `other` is of the same kind as a piece tagged `tags`. */
export function sameKind(tags: readonly string[], other: readonly string[]): boolean {
  if (!tags.length || !other.length) return false;
  const list = similarList(tags);
  if (list && takes(list, other)) return true;
  const own = new Set(kindTags(tags));
  return other.some(tag => own.has(tag));
}

/**
 * The other pieces of the same kind in the room that holds `item` — on its floor, under its ceiling or on its face of
 * a wall —, alike to the piece as it is or to `tags` (what it is being replaced with). None outside a room.
 */
export function sameKindInRoom(level: Level, item: Item, tags: readonly string[] = []): Item[] {
  const room = level.rooms.filter(value => roomHolds(level, value, item)).sort((a, b) => a.area - b.area)[0];
  if (!room) return [];
  return level.items.filter(other => other.id !== item.id && !!other.tags?.length && roomHolds(level, room, other)
    && (sameKind(item.tags ?? [], other.tags!) || sameKind(tags, other.tags!)));
}
